<app-background-card title="Use Cases" titleTestId="use-cases-header" testId="use-cases-card">
  <div class="flex flex-wrap gap-3 justify-between mb-4">
    <div class="flex flex-wrap gap-3 items-center">
      <p-button
        [label]="getToggleViewLabel()"
        icon="pi pi-exchange"
        severity="secondary"
        (onClick)="toggleView()"
        data-testid="toggle-use-cases-runs-button"
      />
      @if (!showRunsView()) {
        <p-select
          [options]="agendaItemOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="selectedAgendaItem()"
          (ngModelChange)="selectedAgendaItem.set($event)"
          placeholder="Filter: Agenda Item"
          appendTo="body"
          [showClear]="true"
          class="w-14rem"
        />
        <p-select
          [options]="moduleFunctionOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="selectedModuleFunction()"
          (ngModelChange)="selectedModuleFunction.set($event)"
          placeholder="Filter: Module Function"
          appendTo="body"
          [showClear]="true"
          class="w-14rem"
        />
        <p-select
          [options]="personaOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="selectedPersona()"
          (ngModelChange)="selectedPersona.set($event)"
          placeholder="Filter: Persona"
          appendTo="body"
          [showClear]="true"
          class="w-18rem"
        />
        <p-multiSelect
          [options]="allColumns"
          optionLabel="header"
          [ngModel]="selectedColumns()"
          (ngModelChange)="selectedColumns.set($event)"
          defaultLabel="Show/Hide Columns"
          appendTo="body"
          class="w-16rem"
        />
      }
    </div>
    <p-button
      icon="pi pi-play"
      label="Start New Run"
      [loading]="runLoading()"
      [disabled]="!canStartNewRun()"
      [pTooltip]="generationRunning() ? generationRunningTooltip() : undefined"
      tooltipPosition="top"
      (onClick)="openStartRunDialog()"
      data-testid="start-new-run-button"
    />
  </div>

  @if (loading()) {
    <app-loading />
  } @else if (showRunsView()) {
    <p-table
      [value]="runRows()"
      [paginator]="true"
      [rows]="15"
      [responsiveLayout]="'scroll'"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      data-testid="use-case-runs-table"
    >
      <ng-template #header>
        <tr>
          <th>ID</th>
          <th>Status</th>
          <th>Started At</th>
          <th>Finished At</th>
          <th>Created</th>
          <th>Skipped Duplicates</th>
          <th>Module Functions</th>
          <th>Used Attributes</th>
          <th>Agenda Items</th>
          <th>Error Message</th>
        </tr>
      </ng-template>
      <ng-template #body let-row>
        <tr>
          <td>{{ row.id }}</td>
          <td>{{ row.status || '-' }}</td>
          <td>{{ row.startedAt ? (row.startedAt | date: 'dd.MM.yyyy HH:mm') : '-' }}</td>
          <td>{{ row.finishedAt ? (row.finishedAt | date: 'dd.MM.yyyy HH:mm') : '-' }}</td>
          <td>{{ row.createdCount }}</td>
          <td>{{ row.skippedDuplicateCount }}</td>
          <td>
            <button
              type="button"
              class="text-primary underline cursor-pointer bg-transparent border-0 p-0"
              (click)="showUsedModuleFunctions(row)"
            >
              {{ getRunUsedModuleFunctionSummary(row) }}
            </button>
          </td>
          <td>
            <button
              type="button"
              class="text-primary underline cursor-pointer bg-transparent border-0 p-0"
              (click)="showUsedAttributes(row)"
            >
              {{ getRunUsedAttributeSummary(row) }}
            </button>
          </td>
          <td>{{ row.usedAgendaItemIds?.length ?? 0 }}</td>
          <td style="white-space: normal; overflow-wrap: anywhere">
            {{ row.errorMessage || '-' }}
          </td>
        </tr>
      </ng-template>
      <ng-template #emptymessage>
        <tr>
          <td colspan="10"><app-no-data /></td>
        </tr>
      </ng-template>
    </p-table>
  } @else {
    <p-table
      [value]="filteredRows()"
      [paginator]="true"
      [rows]="15"
      [responsiveLayout]="'scroll'"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      data-testid="use-cases-table"
      dataKey="id"
      [expandedRowKeys]="expandedRows()"
    >
      <ng-template #header>
        <tr>
          <th style="width: 3rem"></th>
          @for (col of selectedColumns(); track col.field) {
            <th class="w-px">{{ col.header }}</th>
          }
        </tr>
      </ng-template>

      <ng-template #body let-row let-expanded="expanded">
        <tr>
          <td>
            <p-button
              type="button"
              [pRowToggler]="row"
              [text]="true"
              [rounded]="true"
              [plain]="true"
              [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            />
          </td>
          @for (col of selectedColumns(); track col.field) {
            <td class="align-top">
              @switch (col.field) {
                @case ('agendaItemId') {
                  <span
                    class="block max-w-12rem truncate"
                    [title]="getAgendaItemLabel(row.agendaItemId)"
                    >{{ getAgendaItemLabel(row.agendaItemId) }}</span
                  >
                }
                @case ('moduleFunctionId') {
                  <span
                    class="block max-w-12rem truncate"
                    [title]="getModuleFunctionLabel(row.moduleFunctionId)"
                    >{{ getModuleFunctionLabel(row.moduleFunctionId) }}</span
                  >
                }
                @case ('personaId') {
                  <span
                    class="block max-w-10rem truncate"
                    [title]="getPersonaLabel(row.personaId)"
                    >{{ getPersonaLabel(row.personaId) }}</span
                  >
                }
                @case ('enabled') {
                  <input
                    type="checkbox"
                    [checked]="row.enabled"
                    class="h-4 w-4 cursor-pointer accent-primary"
                    (change)="updateEnabled(row.id, $any($event.target).checked)"
                    [disabled]="isUpdatingEnabled(row.id)"
                  />
                }
                @case ('edit') {
                  <p-button
                    label="Edit"
                    size="small"
                    severity="secondary"
                    icon="pi pi-pencil"
                    (onClick)="openEditDialog(row)"
                  />
                }
                @case ('createdAt') {
                  <span class="whitespace-nowrap">{{
                    row.createdAt | date: 'dd.MM.yyyy HH:mm'
                  }}</span>
                }
                @case ('subcriteriaAttributeIds') {
                  @if ((row.subcriteriaAttributeIds?.length ?? 0) > 0) {
                    <button
                      type="button"
                      class="text-primary underline cursor-pointer bg-transparent border-0 p-0"
                      (click)="openAttributeDialog(row)"
                    >
                      {{ row.subcriteriaAttributeIds?.length ?? 0 }}
                    </button>
                  } @else {
                    0
                  }
                }
                @default {
                  <span class="block max-w-10rem truncate" [title]="row[col.field]">{{
                    row[col.field]
                  }}</span>
                }
              }
            </td>
          }
        </tr>
      </ng-template>

      <ng-template #expandedrow let-row>
        <tr>
          <td class="p-0! bg-surface-800"></td>
          <td
            [attr.colspan]="selectedColumns().length"
            class="p-0!"
            style="white-space: normal !important"
          >
            <div
              class="flex flex-col gap-2 p-4 bg-surface-800 text-color w-full max-w-full overflow-hidden"
            >
              <div class="font-semibold text-color">Beschreibung</div>
              <div
                class="w-full max-w-full text-color"
                style="
                  white-space: pre-wrap !important;
                  overflow-wrap: anywhere;
                  word-break: break-word;
                "
              >
                {{ row.descriptionDe || 'Keine Beschreibung vorhanden.' }}
              </div>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template #emptymessage>
        <tr>
          <td [attr.colspan]="selectedColumns().length + 1"><app-no-data /></td>
        </tr>
      </ng-template>
    </p-table>
  }
</app-background-card>

<p-dialog
  [visible]="generationDialogVisible()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{width: '40rem'}"
  header="Start Use Case Generation"
  (onHide)="closeStartRunDialog()"
>
  <div class="flex flex-col gap-3">
    <div>Select module functions to generate use cases for (leave empty to generate for all).</div>
    <p-multiSelect
      [options]="generationModuleFunctions()"
      optionLabel="label"
      optionValue="value"
      [ngModel]="selectedGenerationModuleFunctionIds()"
      (ngModelChange)="selectedGenerationModuleFunctionIds.set($event)"
      [filter]="true"
      filterBy="label"
      defaultLabel="Select module functions"
      class="w-full"
      appendTo="body"
      data-testid="generation-module-function-select"
    />
  </div>

  <div class="flex justify-end gap-2 mt-4">
    <p-button
      label="Cancel"
      severity="secondary"
      [disabled]="runLoading()"
      (onClick)="closeStartRunDialog()"
    />
    <p-button
      icon="pi pi-play"
      label="Start Run"
      [loading]="runLoading()"
      [disabled]="!canStartNewRun()"
      (onClick)="startNewRunFromDialog()"
      data-testid="confirm-start-run-button"
    />
  </div>
</p-dialog>

<p-dialog
  [visible]="runDetailsDialogVisible()"
  (visibleChange)="runDetailsDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{width: '38rem'}"
  [header]="runDetailsDialogTitle()"
  (onHide)="closeRunDetailsDialog()"
>
  @if (runDetailsItems().length > 0) {
    <ul class="m-0 p-0 list-none flex flex-col gap-2 max-h-25rem overflow-auto">
      @for (item of runDetailsItems(); track item) {
        <li class="bg-surface-800 dark:bg-surface-200 rounded-sm px-3 py-2 text-color">
          {{ item }}
        </li>
      }
    </ul>
  } @else {
    <app-no-data />
  }
</p-dialog>

<p-dialog
  [visible]="editDialogVisible()"
  (visibleChange)="editDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{width: '52rem'}"
  header="Edit Use Case"
  (onHide)="closeEditDialog()"
>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <label class="font-medium" for="edit-use-case-title-de">Title (DE)</label>
      <input
        id="edit-use-case-title-de"
        type="text"
        pInputText
        class="w-full"
        [ngModel]="editingTitleDe()"
        (ngModelChange)="editingTitleDe.set($event)"
      />
      <div class="flex justify-end">
        <p-button
          label="Generate title"
          icon="pi pi-refresh"
          severity="secondary"
          [loading]="generateTitleLoading()"
          [disabled]="editSaving()"
          (onClick)="generateTitleDe()"
        />
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <label class="font-medium" for="edit-use-case-description-de">Description (DE)</label>
      <textarea
        id="edit-use-case-description-de"
        rows="10"
        pTextarea
        class="w-full"
        style="resize: vertical; white-space: pre-wrap"
        [ngModel]="editingDescriptionDe()"
        (ngModelChange)="editingDescriptionDe.set($event)"
      ></textarea>
      <div class="flex justify-end">
        <p-button
          label="Generate description"
          icon="pi pi-refresh"
          severity="secondary"
          [loading]="generateDescriptionLoading()"
          [disabled]="editSaving()"
          (onClick)="generateDescriptionDe()"
        />
      </div>
    </div>

    <div class="flex flex-col gap-2 w-12rem">
      <label class="font-medium" for="edit-use-case-duration-min">Duration (min)</label>
      <input
        id="edit-use-case-duration-min"
        type="number"
        min="1"
        pInputText
        class="w-full"
        [ngModel]="editingDurationMin()"
        (ngModelChange)="editingDurationMin.set($event)"
      />
    </div>
  </div>

  <div class="flex justify-end gap-2 mt-4">
    <p-button
      label="Cancel"
      severity="secondary"
      [disabled]="editSaving()"
      (onClick)="closeEditDialog()"
    />
    <p-button label="Save" [loading]="editSaving()" (onClick)="saveEditedUseCase()" />
  </div>
</p-dialog>

<p-dialog
  [visible]="attributeDialogVisible()"
  (visibleChange)="attributeDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{width: '34rem'}"
  header="Use-Case Attribute"
  (onHide)="closeAttributeDialog()"
>
  <div class="flex flex-col gap-3">
    <div class="font-semibold">{{ selectedAttributeDialogTitle() }}</div>
    @if (selectedAttributeNames().length > 0) {
      <ul class="m-0 p-0 list-none flex flex-col gap-2">
        @for (attributeName of selectedAttributeNames(); track attributeName) {
          <li class="bg-surface-800 dark:bg-surface-200 rounded-sm px-3 py-2 text-color">
            {{ attributeName }}
          </li>
        }
      </ul>
    } @else {
      <div>Keine Attribute vorhanden.</div>
    }
  </div>
</p-dialog>
